<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';
import {
  getSortedAgentsByAvailability,
  getAgentsByUpdatedPresence,
} from 'dashboard/helper/agentHelper.js';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import MenuItem from 'dashboard/components/widgets/conversation/contextMenu/menuItem.vue';
import MenuItemWithSubmenu from 'dashboard/components/widgets/conversation/contextMenu/menuItemWithSubmenu.vue';
import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { checkMissingAttributes } = useConversationRequiredAttributes();
const { isAdmin } = useAdmin();

const arrowDownButtonRef = ref(null);
const suspendConfirmDialogRef = ref(null);
const isLoading = ref(false);
const resolveAttributesModalRef = ref(null);

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => toggleDropdown(true);

const currentChat = computed(() => getters.getSelectedChat.value);
const currentUser = computed(() => getters.getCurrentUser.value);
const currentAccountId = computed(() => getters.getCurrentAccountId.value);
const teams = computed(() => store.getters['teams/getTeams']);

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);
const isSuspended = computed(() =>
  Boolean(currentChat.value.service_suspended)
);

const isSilenced = computed(() => Boolean(currentChat.value.silenced));

const showAdditionalActions = computed(
  () =>
    !isPending.value &&
    !isSnoozed.value &&
    (!isSuspended.value || isAdmin.value)
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const showSuspendedLocked = computed(() => isSuspended.value && !isAdmin.value);

const showResolveForSuspendedAdmin = computed(
  () => isSuspended.value && isAdmin.value
);

const onSuspendConversation = async () => {
  closeDropdown();
  await nextTick();
  suspendConfirmDialogRef.value?.open();
};

const isFavorited = computed(() => Boolean(currentChat.value?.favorited));

const agentMenuConfig = computed(() => ({
  icon: 'person-add',
  label: t('CONVERSATION.CARD_CONTEXT_MENU.ASSIGN_AGENT'),
}));

const teamMenuConfig = computed(() => ({
  icon: 'people-team-add',
  label: t('CONVERSATION.CARD_CONTEXT_MENU.ASSIGN_TEAM'),
}));

const favoriteOption = computed(() => ({
  icon: 'star',
  label: isFavorited.value
    ? t('CHAT_LIST.UNFAVORITE_CONVERSATION')
    : t('CHAT_LIST.FAVORITE_CONVERSATION'),
}));

const assignableAgents = computed(() => {
  const inboxId = currentChat.value?.inbox_id;
  if (!inboxId) return [];

  const agents =
    store.getters['inboxAssignableAgents/getAssignableAgents'](inboxId);
  const agentsByUpdatedPresence = getAgentsByUpdatedPresence(
    agents,
    currentUser.value,
    currentAccountId.value
  );
  const filteredAgents = getSortedAgentsByAvailability(agentsByUpdatedPresence);

  return [
    {
      confirmed: true,
      name: t('TEAMS_SETTINGS.LIST.NONE'),
      id: null,
      role: 'agent',
      account_id: 0,
      email: 'None',
    },
    ...filteredAgents,
  ];
});

const teamsList = computed(() => {
  if (currentChat.value?.meta?.team) {
    return [{ id: 0, name: t('TEAMS_SETTINGS.LIST.NONE') }, ...teams.value];
  }
  return teams.value;
});

watch(showActionsDropdown, open => {
  const inboxId = currentChat.value?.inbox_id;
  if (open && inboxId) {
    store.dispatch('inboxAssignableAgents/fetch', [inboxId]);
  }
});

const generateMenuLabelConfig = (option, type = 'text') => ({
  key: option.id,
  ...(type === 'agent' && { thumbnail: option.thumbnail }),
  ...(type === 'agent' && { status: option.availability_status }),
  ...(type === 'agent' && { label: option.name }),
  ...(type === 'team' && { label: option.name }),
});

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const openSnoozeModal = () => {
  const ninja = document.querySelector('ninja-keys');
  ninja.open({ parent: 'snooze_conversation' });
};

const toggleStatus = (status, snoozedUntil, customAttributes = null) => {
  closeDropdown();
  isLoading.value = true;

  const payload = {
    conversationId: currentChat.value.id,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store.dispatch('toggleStatus', payload).then(() => {
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
    isLoading.value = false;
  });
};

const confirmSuspendConversation = () => {
  suspendConfirmDialogRef.value?.close();
  toggleStatus(wootConstants.STATUS_TYPE.SUSPENDED);
};

const onAssignAgent = agent => {
  closeDropdown();
  const agentId = agent?.id || null;
  store.dispatch('setCurrentChatAssignee', {
    conversationId: currentChat.value.id,
    assignee: agentId ? agent : null,
  });
  store
    .dispatch('assignAgent', {
      conversationId: currentChat.value.id,
      agentId,
    })
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_AGENT'));
    });
};

const onAssignTeam = team => {
  closeDropdown();
  const teamId = team?.id || 0;
  store.dispatch('setCurrentChatTeam', {
    team: teamId ? team : null,
    conversationId: currentChat.value.id,
  });
  store
    .dispatch('assignTeam', {
      conversationId: currentChat.value.id,
      teamId,
    })
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_TEAM'));
    });
};

const onToggleFavorite = () => {
  closeDropdown();
  store.dispatch('toggleFavorite', {
    conversationId: currentChat.value.id,
    favorited: isFavorited.value,
  });
};

const onSilenceConversation = () => {
  closeDropdown();
  store.dispatch('silenceConversation', currentChat.value.id).then(() => {
    useAlert(t('CONTACT_PANEL.SILENCED_SUCCESS'));
  });
};

const onUnsilenceConversation = () => {
  closeDropdown();
  store.dispatch('unsilenceConversation', currentChat.value.id).then(() => {
    useAlert(t('CONTACT_PANEL.UNSILENCED_SUCCESS'));
  });
};

const handleResolveWithAttributes = ({ attributes, context }) => {
  if (context) {
    const currentCustomAttributes = currentChat.value.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };
    toggleStatus(
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

const onCmdUnsuspendConversation = () => {
  closeDropdown();
  isLoading.value = true;
  store
    .dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      serviceSuspended: false,
    })
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS'));
      isLoading.value = false;
    });
};

const onCmdResolveConversation = () => {
  const currentCustomAttributes = currentChat.value.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    const conversationContext = {
      id: currentChat.value.id,
      snoozedUntil: null,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
  }
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async () => {
      onCmdResolveConversation();
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      onCmdResolveConversation();

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
</script>

<template>
  <div class="flex relative justify-end items-center resolve-actions">
    <ButtonGroup
      class="flex-shrink-0 rounded-lg shadow outline-1 outline"
      :class="!showOpenButton ? 'outline-n-container' : 'outline-transparent'"
    >
      <Button
        v-if="showSuspendedLocked"
        :label="t('CONVERSATION.HEADER.SUSPENDED_BADGE')"
        size="sm"
        color="ruby"
        no-animation
        disabled
      />
      <Button
        v-else-if="isOpen"
        :label="t('CONVERSATION.HEADER.RESOLVE_ACTION')"
        size="sm"
        color="slate"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="onCmdResolveConversation"
      />
      <Button
        v-else-if="isResolved"
        :label="t('CONVERSATION.HEADER.REOPEN_ACTION')"
        size="sm"
        color="slate"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-else-if="showOpenButton"
        :label="t('CONVERSATION.HEADER.OPEN_ACTION')"
        size="sm"
        color="slate"
        no-animation
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-if="showAdditionalActions"
        ref="arrowDownButtonRef"
        icon="i-lucide-chevron-down"
        :disabled="isLoading"
        size="sm"
        no-animation
        class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
        color="slate"
        trailing-icon
        @click="openDropdown"
      />
    </ButtonGroup>
    <div
      v-if="showActionsDropdown"
      v-on-clickaway="closeDropdown"
      class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-10 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 min-w-[15rem]"
    >
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem v-if="showResolveForSuspendedAdmin">
          <Button
            :label="t('CONVERSATION.CARD_CONTEXT_MENU.REACTIVATE')"
            ghost
            ruby
            sm
            start
            icon="i-lucide-play-circle"
            class="w-full"
            @click="onCmdUnsuspendConversation"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="showResolveForSuspendedAdmin && !isOpen">
          <Button
            :label="t('CONVERSATION.HEADER.REOPEN_ACTION')"
            ghost
            slate
            sm
            start
            icon="i-lucide-arrow-redo"
            class="w-full"
            @click="onCmdOpenConversation"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="showResolveForSuspendedAdmin && isOpen">
          <Button
            :label="t('CONVERSATION.HEADER.RESOLVE_ACTION')"
            ghost
            slate
            sm
            start
            icon="i-lucide-checkmark"
            class="w-full"
            @click="onCmdResolveConversation"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL')"
            ghost
            slate
            sm
            start
            icon="i-lucide-alarm-clock-minus"
            class="w-full"
            @click="() => openSnoozeModal()"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING')"
            ghost
            slate
            sm
            start
            icon="i-lucide-circle-dot-dashed"
            class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.PENDING)"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isSuspended">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.SUSPEND')"
            ghost
            slate
            sm
            start
            icon="i-lucide-pause-circle"
            class="w-full"
            @click="onSuspendConversation"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isSilenced">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.SILENCE')"
            ghost
            slate
            sm
            start
            icon="i-lucide-bell-off"
            class="w-full"
            @click="onSilenceConversation"
          />
        </WootDropdownItem>
        <WootDropdownItem v-else>
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.UNSILENCE')"
            ghost
            slate
            sm
            start
            icon="i-lucide-bell"
            class="w-full"
            @click="onUnsilenceConversation"
          />
        </WootDropdownItem>
        <hr class="my-1 border-b border-n-weak dark:border-n-weak" />
        <MenuItemWithSubmenu
          :option="agentMenuConfig"
          :sub-menu-available="assignableAgents.length > 0"
        >
          <MenuItem
            v-for="agent in assignableAgents"
            :key="agent.id ?? 'none'"
            :option="generateMenuLabelConfig(agent, 'agent')"
            variant="agent"
            @click.stop="onAssignAgent(agent)"
          />
        </MenuItemWithSubmenu>
        <MenuItemWithSubmenu
          :option="teamMenuConfig"
          :sub-menu-available="teamsList.length > 0"
        >
          <MenuItem
            v-for="team in teamsList"
            :key="team.id"
            :option="generateMenuLabelConfig(team, 'team')"
            variant="agent"
            @click.stop="onAssignTeam(team)"
          />
        </MenuItemWithSubmenu>
        <MenuItem
          :option="favoriteOption"
          variant="icon"
          @click.stop="onToggleFavorite"
        />
      </WootDropdownMenu>
    </div>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
    <Dialog
      ref="suspendConfirmDialogRef"
      type="alert"
      :title="t('CONVERSATION.RESOLVE_DROPDOWN.SUSPEND')"
      :description="t('CONVERSATION.RESOLVE_DROPDOWN.SUSPEND_CONFIRM')"
      :confirm-button-label="
        t('CONVERSATION.RESOLVE_DROPDOWN.SUSPEND_CONFIRM_OK')
      "
      :cancel-button-label="
        t('CONVERSATION.RESOLVE_DROPDOWN.SUSPEND_CONFIRM_CANCEL')
      "
      @confirm="confirmSuspendConversation"
    />
  </div>
</template>
